import { useMemo, useRef, useState } from 'react';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { AppShell, Button } from '@dj/ui';
import { callable } from '@dj/firebase-client';
import type {
  ProviderOrgType,
  RequestProviderInput,
  RequestProviderResponse,
} from '@dj/types';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

const ORG_TYPES: { value: ProviderOrgType; label: string; memberLabel: string }[] = [
  { value: 'medical', label: 'Medical provider (GP / clinic)', memberLabel: 'patients' },
  { value: 'iwi_hapu', label: 'Iwi or Hapū', memberLabel: 'whānau' },
  { value: 'employer', label: 'Employer / business', memberLabel: 'employees' },
  { value: 'school', label: 'School / education', memberLabel: 'students' },
  { value: 'ngo', label: 'NGO / charity', memberLabel: 'beneficiaries' },
  { value: 'government', label: 'Government agency', memberLabel: 'members' },
  { value: 'other', label: 'Other', memberLabel: 'members' },
];

const SUBSIDY_PRESETS = [25, 50, 75, 100] as const;
const MONTHLY_PRICE_NZD = 10;

interface FormState {
  // org
  orgName: string;
  orgType: ProviderOrgType | '';
  specialty: string;
  websiteUrl: string;
  nzbn: string;
  // address
  street: string;
  suburb: string;
  city: string;
  postcode: string;
  region: string;
  // members
  estimatedCount: string;
  subsidyChoice: '25' | '50' | '75' | '100' | 'other' | '';
  subsidyOther: string;
  rosterTiming: 'now' | 'within_week' | 'within_month' | 'need_template' | '';
  // primary contact
  pcName: string;
  pcPosition: string;
  pcEmail: string;
  pcMobile: string;
  // billing contact
  billingSameAsPrimary: boolean;
  bcName: string;
  bcEmail: string;
  bcMobile: string;
  billingDay: '1' | '15';
  // misc
  notes: string;
  authority: boolean;
  privacy: boolean;
}

const INITIAL: FormState = {
  orgName: '', orgType: '', specialty: '', websiteUrl: '', nzbn: '',
  street: '', suburb: '', city: '', postcode: '', region: '',
  estimatedCount: '', subsidyChoice: '', subsidyOther: '', rosterTiming: '',
  pcName: '', pcPosition: '', pcEmail: '', pcMobile: '',
  billingSameAsPrimary: true, bcName: '', bcEmail: '', bcMobile: '', billingDay: '1',
  notes: '', authority: false, privacy: false,
};

export function RequestProviderPage() {
  const [f, setF] = useState<FormState>(INITIAL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const memberLabel = useMemo(() => {
    const found = ORG_TYPES.find((t) => t.value === f.orgType);
    return found?.memberLabel || 'members';
  }, [f.orgType]);

  const subsidyPercent = useMemo(() => {
    if (f.subsidyChoice === 'other') return Number(f.subsidyOther) || 0;
    if (f.subsidyChoice) return Number(f.subsidyChoice);
    return 0;
  }, [f.subsidyChoice, f.subsidyOther]);

  const monthlyCost = useMemo(() => {
    const count = Number(f.estimatedCount) || 0;
    if (count <= 0 || subsidyPercent <= 0) return null;
    return (count * MONTHLY_PRICE_NZD * subsidyPercent) / 100;
  }, [f.estimatedCount, subsidyPercent]);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setF((prev) => ({ ...prev, [k]: v }));
  };

  const submit = async () => {
    setError(null);

    // Local validation — give friendly error before the server says "invalid_payload".
    if (!f.orgName.trim()) return setError('Organization name is required.');
    if (!f.orgType) return setError('Please select an organization type.');
    if (f.orgType === 'other' && !f.specialty.trim()) return setError('Please describe your sector.');
    if (!f.street.trim() || !f.city.trim() || !f.postcode.trim()) return setError('Physical address is required (street, city, postcode).');
    if (!Number(f.estimatedCount) || Number(f.estimatedCount) < 1) return setError(`Estimated ${memberLabel} count must be a positive number.`);
    if (!f.subsidyChoice) return setError('Please select a subsidy tier.');
    if (f.subsidyChoice === 'other' && (!Number(f.subsidyOther) || Number(f.subsidyOther) < 1 || Number(f.subsidyOther) > 100)) {
      return setError('Custom subsidy must be between 1 and 100.');
    }
    if (!f.rosterTiming) return setError('Please tell us when you can provide your roster.');
    if (!f.pcName.trim() || !f.pcEmail.trim() || !f.pcEmail.includes('@')) return setError('Primary contact name + valid email required.');
    if (!f.billingSameAsPrimary) {
      if (!f.bcName.trim() || !f.bcEmail.trim() || !f.bcEmail.includes('@')) return setError('Billing contact name + valid email required.');
    }
    if (!f.authority) return setError('Please confirm you have authority to commit your organization.');
    if (!f.privacy) return setError('Please confirm you have read the Privacy Policy.');
    if (TURNSTILE_SITE_KEY && !turnstileToken) return setError('Please complete the verification challenge.');

    const payload: RequestProviderInput = {
      organization: {
        name: f.orgName.trim(),
        type: f.orgType as ProviderOrgType,
        specialty: f.orgType === 'other' ? f.specialty.trim() : undefined,
        websiteUrl: f.websiteUrl.trim() || undefined,
        nzbn: f.nzbn.trim() || undefined,
      },
      address: {
        street: f.street.trim(),
        suburb: f.suburb.trim() || undefined,
        city: f.city.trim(),
        postcode: f.postcode.trim(),
        region: f.region.trim() || undefined,
      },
      members: {
        estimatedCount: Number(f.estimatedCount),
        subsidyPercent,
        rosterTiming: f.rosterTiming as RequestProviderInput['members']['rosterTiming'],
      },
      primaryContact: {
        name: f.pcName.trim(),
        position: f.pcPosition.trim() || undefined,
        email: f.pcEmail.trim(),
        mobile: f.pcMobile.trim() || undefined,
      },
      billingContact: f.billingSameAsPrimary
        ? undefined
        : {
            name: f.bcName.trim(),
            email: f.bcEmail.trim(),
            mobile: f.bcMobile.trim() || undefined,
          },
      billingDay: Number(f.billingDay) as 1 | 15,
      notes: f.notes.trim() || undefined,
      consents: { authority: f.authority, privacy: f.privacy },
      turnstileToken: turnstileToken ?? undefined,
    };

    setBusy(true);
    try {
      const requestProvider = callable<RequestProviderInput, RequestProviderResponse>('requestProvider');
      const r = await requestProvider(payload);
      if (!r.success) {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        const msg = r.reason === 'captcha_failed'
          ? 'Verification failed — please try again.'
          : r.reason === 'rate_limited'
          ? 'Too many submissions — please wait a moment and try again.'
          : 'Some details look invalid. Please review the form and try again.';
        setError(msg);
        return;
      }
      setSubmittedId(r.requestId || 'queued');
    } catch (e: any) {
      setError(e?.message ?? 'Could not submit right now. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (submittedId) {
    return (
      <AppShell title="Request received">
        <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-3">Kia ora — we've got it.</h1>
          <p className="text-slate-600 mb-2">
            We've received your request and will review it within 24 hours.
          </p>
          <p className="text-slate-600 mb-6">
            You'll get an email at <strong>{f.pcEmail}</strong> with your unique provider code and next steps.
          </p>
          <p className="text-xs text-slate-400">Reference: {submittedId}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Set up Doctor's Journal for your organization">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 max-w-2xl mx-auto mb-4 text-sm text-amber-900">
        <strong>Heads up:</strong> we'll review your request within 24 hours and email you a unique provider code.
      </div>
      <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-2xl mx-auto space-y-8">
        <header>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Tell us about your organization</h1>
          <p className="text-slate-600 text-sm">
            Doctor's Journal helps your {memberLabel === 'members' ? 'people' : memberLabel} record + remember every doctor visit.
            You subsidise some or all of their subscription. Below covers everything we need to get you set up.
          </p>
        </header>

        {/* Organization */}
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-slate-800 border-b border-slate-200 pb-1">Organization</h2>
          <Field label="Organization name">
            <input type="text" value={f.orgName} onChange={(e) => update('orgName', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Organization type">
            <select value={f.orgType} onChange={(e) => update('orgType', e.target.value as ProviderOrgType | '')} className={inputCls}>
              <option value="">Select…</option>
              {ORG_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>
          {f.orgType === 'other' && (
            <Field label="Sector / specialty">
              <input type="text" value={f.specialty} onChange={(e) => update('specialty', e.target.value)} className={inputCls} placeholder="e.g. iwi health trust, mental health charity" />
            </Field>
          )}
          <Field label="Website (optional)">
            <input type="url" value={f.websiteUrl} onChange={(e) => update('websiteUrl', e.target.value)} className={inputCls} placeholder="https://" />
          </Field>
          <Field label="NZBN (optional)" hint="NZ Business Number — helps us cross-check at nzbn.govt.nz">
            <input type="text" value={f.nzbn} onChange={(e) => update('nzbn', e.target.value)} className={inputCls} placeholder="13-digit number" />
          </Field>
        </section>

        {/* Address */}
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-slate-800 border-b border-slate-200 pb-1">Physical address</h2>
          <Field label="Street">
            <input type="text" value={f.street} onChange={(e) => update('street', e.target.value)} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Suburb (optional)"><input type="text" value={f.suburb} onChange={(e) => update('suburb', e.target.value)} className={inputCls} /></Field>
            <Field label="City"><input type="text" value={f.city} onChange={(e) => update('city', e.target.value)} className={inputCls} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Postcode"><input type="text" value={f.postcode} onChange={(e) => update('postcode', e.target.value)} className={inputCls} /></Field>
            <Field label="Region (optional)"><input type="text" value={f.region} onChange={(e) => update('region', e.target.value)} className={inputCls} placeholder="e.g. Waikato, Auckland" /></Field>
          </div>
        </section>

        {/* Members */}
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-slate-800 border-b border-slate-200 pb-1">Your {memberLabel}</h2>
          <Field label={`Estimated number of ${memberLabel}`}>
            <input type="number" min={1} value={f.estimatedCount} onChange={(e) => update('estimatedCount', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Subsidy tier — how much of the $10/month/person are you covering?">
            <div className="grid grid-cols-5 gap-2">
              {SUBSIDY_PRESETS.map((p) => (
                <button key={p} type="button" onClick={() => update('subsidyChoice', String(p) as FormState['subsidyChoice'])}
                  className={`py-2 rounded text-sm font-medium ${f.subsidyChoice === String(p) ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                  {p}%
                </button>
              ))}
              <button type="button" onClick={() => update('subsidyChoice', 'other')}
                className={`py-2 rounded text-sm font-medium ${f.subsidyChoice === 'other' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                Other
              </button>
            </div>
            {f.subsidyChoice === 'other' && (
              <input type="number" min={1} max={100} value={f.subsidyOther} onChange={(e) => update('subsidyOther', e.target.value)} className={inputCls + ' mt-2'} placeholder="Custom % (1-100)" />
            )}
          </Field>
          {monthlyCost !== null && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-900">
              <strong>Estimated monthly cost:</strong> ${monthlyCost.toFixed(2)} NZD
              <span className="text-emerald-700"> ({f.estimatedCount} {memberLabel} × $10 × {subsidyPercent}%)</span>
            </div>
          )}
          <Field label="When can you provide your roster?">
            <select value={f.rosterTiming} onChange={(e) => update('rosterTiming', e.target.value as FormState['rosterTiming'])} className={inputCls}>
              <option value="">Select…</option>
              <option value="now">Right now (we have the list ready)</option>
              <option value="within_week">Within 1 week</option>
              <option value="within_month">Within 1 month</option>
              <option value="need_template">We need the template first</option>
            </select>
          </Field>
          <a href="/roster-template.csv" download className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 underline">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            Download roster spreadsheet template (CSV)
          </a>
        </section>

        {/* Primary contact */}
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-slate-800 border-b border-slate-200 pb-1">Primary contact</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name"><input type="text" value={f.pcName} onChange={(e) => update('pcName', e.target.value)} className={inputCls} /></Field>
            <Field label="Position / title"><input type="text" value={f.pcPosition} onChange={(e) => update('pcPosition', e.target.value)} className={inputCls} placeholder="e.g. Practice Manager" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email"><input type="email" value={f.pcEmail} onChange={(e) => update('pcEmail', e.target.value)} className={inputCls} /></Field>
            <Field label="Mobile (optional)"><input type="tel" value={f.pcMobile} onChange={(e) => update('pcMobile', e.target.value)} className={inputCls} placeholder="+64…" /></Field>
          </div>
        </section>

        {/* Billing contact */}
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-slate-800 border-b border-slate-200 pb-1">Billing contact</h2>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.billingSameAsPrimary} onChange={(e) => update('billingSameAsPrimary', e.target.checked)} />
            <span>Same as primary contact</span>
          </label>
          {!f.billingSameAsPrimary && (
            <>
              <Field label="Billing name"><input type="text" value={f.bcName} onChange={(e) => update('bcName', e.target.value)} className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Billing email"><input type="email" value={f.bcEmail} onChange={(e) => update('bcEmail', e.target.value)} className={inputCls} /></Field>
                <Field label="Billing mobile (optional)"><input type="tel" value={f.bcMobile} onChange={(e) => update('bcMobile', e.target.value)} className={inputCls} /></Field>
              </div>
            </>
          )}
          <Field label="Preferred billing day of month">
            <div className="flex gap-2">
              {(['1', '15'] as const).map((d) => (
                <button key={d} type="button" onClick={() => update('billingDay', d)}
                  className={`px-4 py-2 rounded text-sm font-medium ${f.billingDay === d ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                  {d}{d === '1' ? 'st' : 'th'}
                </button>
              ))}
            </div>
          </Field>
        </section>

        {/* Notes */}
        <section className="space-y-3">
          <h2 className="text-base font-semibold text-slate-800 border-b border-slate-200 pb-1">Notes or questions</h2>
          <textarea value={f.notes} onChange={(e) => update('notes', e.target.value)} rows={3}
            className={inputCls} placeholder="Anything else we should know? Questions about the setup or pricing?" />
        </section>

        {/* Consent */}
        <section className="space-y-2">
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input type="checkbox" checked={f.authority} onChange={(e) => update('authority', e.target.checked)} className="mt-1" />
            <span>I confirm that I have authority to commit my organization to this subscription.</span>
          </label>
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input type="checkbox" checked={f.privacy} onChange={(e) => update('privacy', e.target.checked)} className="mt-1" />
            <span>
              I've read the <a href="https://brucysnz.github.io/doctorsjournal-legal/privacy-policy.html" target="_blank" rel="noopener" className="text-indigo-600 underline">Privacy Policy</a> and understand member data is encrypted and stored in New Zealand region.
            </span>
          </label>
        </section>

        {TURNSTILE_SITE_KEY && (
          <div>
            <Turnstile siteKey={TURNSTILE_SITE_KEY} ref={turnstileRef} onSuccess={(t) => setTurnstileToken(t)} options={{ theme: 'light' }} />
          </div>
        )}

        {error && <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-900">{error}</div>}

        <Button onClick={submit} disabled={busy} className="w-full">
          {busy ? 'Submitting…' : 'Submit request'}
        </Button>

        <p className="text-xs text-center text-slate-400">
          We'll review your request within 24 hours and email you the next steps.
        </p>
      </div>
    </AppShell>
  );
}

const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}
